import { useQuery, useMutation } from "@apollo/client/react";
import { processEntityDataFromApi } from "@/lib/utils/crm/task-data-processing";
import { prepareEntityGraphQlMutationInput } from "@/lib/utils/crm/prepare-graphql-entity-payload";
import {
  GetContactssDocument,
  GetContactsDocument,
  CreateContactsDocument,
  UpdateContactsDocument,
  DeleteContactsDocument,
  GetContactssQuery,
  GetContactsQuery,
  CreateContactsMutation,
  UpdateContactsMutation,
  DeleteContactsMutation,
  ContactsInsertInput,
  ContactsFilter,
  Contacts,
} from "@/src/gql/graphql";
import { ENHANCED_CONTACTS_CONFIG } from "@/features/contacts/components/EnhancedContactsConfig";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
// Contact/company writes go through pg_graphql, not an API route, so the
// server-side mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

function parseContactFromApi(node: Contacts): Contacts {
  return processEntityDataFromApi(node, ENHANCED_CONTACTS_CONFIG.fields);
}

/**
 * Add "not a company line" to whatever filter the caller built.
 *
 * A COMPANY LINE ANCHOR is a `contacts` row that exists only to hold a shared
 * front desk's history — it is not a person (see CONTACT_METHOD_EXCLUSIVITY).
 * Excluding it in the FILTER rather than after the fetch matters: this query's
 * `totalCount` is the contact count the CRM displays, and a client-side drop
 * would leave the count inflated and the page sizes short. The partial index
 * `idx_contacts_is_company_line` covers the predicate.
 *
 * A caller that explicitly filters on `is_company_line` (the promote-to-anchor
 * admin view) is left alone.
 */
function excludeCompanyLineAnchors(
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const existing = params?.filter;
  const filter: ContactsFilter =
    existing && typeof existing === "object" ? (existing as ContactsFilter) : {};
  if (filter.is_company_line) return params;
  return { ...params, filter: { ...filter, is_company_line: { eq: false } } };
}

// Contact hooks using generated GraphQL operations
export const useContactHooks = (): EntityHooks<Contacts> => ({
  queryDocument: GetContactssDocument,
  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetContactssDocument,
      excludeCompanyLineAnchors(params),
      (data: GetContactssQuery) => data.contactsCollection,
      (node) => parseContactFromApi(node as Contacts)
    ),
  useInfinite: (_params?: Record<string, unknown>) => {
    return {
      data: undefined,
      isLoading: false,
      error: null,
      refetch: () => {},
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
    };
  },
  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetContactsQuery>(GetContactsDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || options?.enabled === false,
    });
    return {
      data: (result.data?.contactsCollection?.edges?.[0]?.node
        ? parseContactFromApi(result.data.contactsCollection.edges[0].node as Contacts)
        : undefined),
      isLoading: result.loading,
      error: result.error || null,
      refetch: result.refetch,
    };
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateContactsMutation>(UpdateContactsDocument);

    return {
      mutateAsync: async ({ id, ...input }: { id: string } & Partial<Contacts>) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          ENHANCED_CONTACTS_CONFIG.fields,
          { mode: "update" }
        );
        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the contact.");

        const updated = data?.updatecontactsCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the contact.");
        return parseContactFromApi(updated as Contacts);
      },
      isPending: result.loading,
    };
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteContactsMutation>(DeleteContactsDocument);
    return {
      mutateAsync: async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the contact.");
        const deletedId = data?.deleteFromcontactsCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the contact.");
        return deletedId;
      },
      isPending: result.loading,
    };
  },
  useCreate: () => {
    const [mutate, result] = useMutation<CreateContactsMutation>(CreateContactsDocument);
    return {
      mutateAsync: async (input: ContactsInsertInput) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          ENHANCED_CONTACTS_CONFIG.fields,
          { mode: "insert" }
        );
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the contact.");
        const created = data?.insertIntocontactsCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the contact.");
        return created as Contacts;
      },
      isPending: result.loading,
    };
  },

  getFieldConfig: (fieldName: string) => {
    return (
      ENHANCED_CONTACTS_CONFIG.fields[fieldName] ||
      null
    );
  },

  getTableConfig: () => {
    return ENHANCED_CONTACTS_CONFIG;
  },
});
